import type {
  AggregationColumnOption,
  AggregationColumnOptionGroup,
  AggregationColumnOptionGroupsByObjectType,
  AggregationFilterOptionByObjectType,
  AggregationPublicVersionByObjectType,
  ClientQueryDataConfigGetObjectsResponse,
  ClientQueryDataConfigObjectVersion,
} from '../../store/engine/types';
import type { ServiceAttributesById, Services } from '../../store/engine/types/schema';
import { isPublishedAttribute } from '../../editor/components/aggregations/helpers';
import {
  AGGREGATION_SERVICE_KEY_IN_SERVICES,
  AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME,
} from 'flow/service/services/constants';

type ConfigObjectDataByObjectType = {
  aggregationFilterOptionsByObjectType: AggregationFilterOptionByObjectType;
  columnOptionGroupsByObjectType: AggregationColumnOptionGroupsByObjectType;
  publicVersionByObjectType: AggregationPublicVersionByObjectType;
};

const helpers = {
  extractConfigObjectDataByObjectType: ({
    clientQueryDataConfigObjects,
  }: {
    clientQueryDataConfigObjects: ClientQueryDataConfigGetObjectsResponse['data_objects'];
  }): ConfigObjectDataByObjectType => clientQueryDataConfigObjects
    .reduce((acc, configObject) => {
      const activeConfigObjectVersion = helpers.getActiveConfigObjectVersion({
        configObjectVersions: configObject.versions,
      });

      if (!activeConfigObjectVersion) {
        return acc;
      }

      const { object_slug: objectSlug } = configObject;

      acc.aggregationFilterOptionsByObjectType[objectSlug] = activeConfigObjectVersion
        .columns
        .filter(column => column.user_filterable)
        .map(column => ({
          label: column.display_name,
          value: column.column_slug,
          ui_data_type: column.ui_data_type,
        }));

      const columnOptions = activeConfigObjectVersion.columns
        .filter(column => column.user_aggregable)
        .map(column => ({
          label: column.display_name,
          value: column.column_slug,
          aggregations: column.aggregations,
          ui_data_type: column.ui_data_type,
        }));

      acc.columnOptionGroupsByObjectType[objectSlug] = helpers.buildColumnOptionGroups(
        columnOptions, configObject.object_display_name,
      );
      acc.publicVersionByObjectType[objectSlug] = activeConfigObjectVersion.public_version;

      return acc;
    }, {
      aggregationFilterOptionsByObjectType: {},
      columnOptionGroupsByObjectType: {},
      publicVersionByObjectType: {},
    } as ConfigObjectDataByObjectType),

  formatAggregationServiceAttributesById: ({
    aggregationFilterOptionsByObjectType,
    aggregationServiceAttributesById,
    columnOptionGroupsByObjectType,
    publicVersionByObjectType,
  }: {
    aggregationFilterOptionsByObjectType: AggregationFilterOptionByObjectType;
    aggregationServiceAttributesById: ServiceAttributesById;
    columnOptionGroupsByObjectType: AggregationColumnOptionGroupsByObjectType;
    publicVersionByObjectType: AggregationPublicVersionByObjectType;
  }): ServiceAttributesById => Object.fromEntries(Object
    .entries(
      aggregationServiceAttributesById,
    )
    .map(([attributeId, attribute]) => {
      const objectType = AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME[attribute.attribute_name];

      if (!objectType) {
        return [attributeId, attribute];
      }

      return [
        attributeId,
        {
          ...attribute,
          aggregationColumnOptionsGrouped: (
            columnOptionGroupsByObjectType[objectType]
          ),
          aggregationFilterOptions: (
            aggregationFilterOptionsByObjectType[objectType]
          ),
          aggregationObjectVersionPublicVersion: (
            publicVersionByObjectType[objectType]
          ),
        },
      ];
    }),
  ),

  buildColumnOptionGroups: (
    columnOptions: AggregationColumnOption[],
    objectDisplayName: string,
  ): AggregationColumnOptionGroup[] => {
    const schemaOptions: AggregationColumnOption[] = [];
    const publishedAttrOptions: AggregationColumnOption[] = [];

    for (const option of columnOptions) {
      if (isPublishedAttribute(option.ui_data_type)) {
        publishedAttrOptions.push(option);
      } else {
        schemaOptions.push(option);
      }
    }

    const groups: AggregationColumnOptionGroup[] = [];

    if (schemaOptions.length > 0) {
      groups.push({
        label: `${objectDisplayName} Schema`,
        value: null,
        options: schemaOptions,
      });
    }

    if (publishedAttrOptions.length > 0) {
      groups.push({
        label: `${objectDisplayName} Published Attributes`,
        value: null,
        options: publishedAttrOptions,
      });
    }

    return groups;
  },

  getActiveConfigObjectVersion: ({
    configObjectVersions,
  }: {
    configObjectVersions: ClientQueryDataConfigObjectVersion[];
  }): ClientQueryDataConfigObjectVersion | undefined => (
    configObjectVersions.find(version => version.active_selects)
  ),

  getAggregationServiceId: (
    { servicesById }: { servicesById: Services },
  ): number | undefined => Object
    .values(servicesById)
    .find(service => (
      service.internal_name === AGGREGATION_SERVICE_KEY_IN_SERVICES),
    )
    ?.id,
};

export const enrichAggregationServiceAttributes = ({
  clientQueryDataConfigObjects,
  servicesById,
}: {
  clientQueryDataConfigObjects: ClientQueryDataConfigGetObjectsResponse['data_objects'];
  servicesById: Services;
}): Services => {
  const aggregationsServiceId = helpers.getAggregationServiceId({ servicesById });

  if (!aggregationsServiceId) {
    return servicesById;
  }

  const aggregationServiceAttributesById = servicesById[aggregationsServiceId].attributes;

  if (!aggregationServiceAttributesById) {
    return servicesById;
  }

  const {
    aggregationFilterOptionsByObjectType,
    columnOptionGroupsByObjectType,
    publicVersionByObjectType,
  } = helpers.extractConfigObjectDataByObjectType({ clientQueryDataConfigObjects });

  const aggregationServiceAttributesWithConfigObjectsById = helpers
    .formatAggregationServiceAttributesById({
      aggregationFilterOptionsByObjectType,
      aggregationServiceAttributesById,
      columnOptionGroupsByObjectType,
      publicVersionByObjectType,
    });

  return {
    ...servicesById,
    [aggregationsServiceId]: {
      ...servicesById[aggregationsServiceId],
      attributes: aggregationServiceAttributesWithConfigObjectsById,
    },
  };
};
